import CtaGroup from '@/features/microsite/components/CtaGroup';
import HeroPill from '@/features/microsite/components/HeroPill';
import MicrositeShell from '@/features/microsite/components/MicrositeShell';
import ProjectDescription, {HighlightNote} from '@/features/microsite/components/ProjectDescription';
import ProjectDescriptionRow from '@/features/microsite/components/ProjectDescriptionRow';
import {formatPublicDates, joinList, joinWithOr} from '@/features/microsite/utils/format';
import ProjectDescriptionTag from '@/components/ui/ProjectDescriptionTag';
import useEventChannel from '@/hooks/useEventChannel';
import usePublicEvent from '@/hooks/usePublicEvent';
import {TrophyIcon} from '@heroicons/react/24/outline';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

export default function ShareEventPage() {
  const {t} = useTranslation();
  const {eventId} = useParams();
  const {event, isLoading, isError, refresh} = usePublicEvent(eventId);

  // Any broadcast means the event changed; re-pull the public record.
  useEventChannel(eventId, () => refresh());

  const location = [event?.city, event?.state].filter(Boolean).join(', ');
  // `date` is `[date]` for a specific date, otherwise `[startDate, endDate]`.
  const dateLabel = formatPublicDates(event?.date?.filter(Boolean));
  const studentAges = event?.student_ages?.length ? event.student_ages.join(', ') : null;
  const commitmentLabel = event?.commitment?.length
    ? joinWithOr(event.commitment.map((v) => t(`projects.microsite.commitment_options.${v}`, v)))
    : null;
  const activityDescription = event && t(`projects.microsite.activity_description.${event.activity}`, {
    defaultValue: t('microsite.event.description.default_activity_description')
  });

  const description = event && (
    <ProjectDescription
      kicker={t('microsite.event.description.kicker')}
      title={event.title}
      summaryTitle={event.activity}
      description={activityDescription}
      completed={event.completed && <CtaGroup
        type="alt"
        heading={<div className="flex gap-2 items-center"><TrophyIcon
          className="bg-[#4E5A20] size-12 text-white rounded-full p-2 shrink-0"/>
          <div>{t('projects.microsite.completed_cta.heading')}</div>
        </div>}
        body={t('projects.microsite.completed_cta.body')}
        buttonLabel={t('projects.microsite.completed_cta.button_label')}
        primaryHref='/signup'
      />}
    >
      <ProjectDescriptionRow label={t('microsite.project.description.where_label')}>
        <span className="flex flex-wrap items-center gap-2">
          {location &&
            <span className="text-base leading-tight text-cs-gray-500">{location}</span>}
          {(event.participation_options || []).map((opt) => (
            <ProjectDescriptionTag key={opt} variant="orange">{opt}</ProjectDescriptionTag>
          ))}
        </span>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.working_with_label')}>
        <span className="flex flex-wrap items-center gap-2">
          {studentAges && <ProjectDescriptionTag>{studentAges}</ProjectDescriptionTag>}
          {event.groupSize && <ProjectDescriptionTag>{event.groupSize}</ProjectDescriptionTag>}
        </span>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.topic_areas_label')}>
        <p className="text-base leading-tight text-cs-gray-500 whitespace-pre-wrap">
          {joinList(event.subject_areas)}
        </p>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.event.description.experience_areas_label')}>
        <div className="flex flex-col gap-3">
          <p className="text-base leading-tight text-cs-gray-500 whitespace-pre-wrap">
            {joinList(event.career_experience)}
          </p>
          <HighlightNote>
            {t('microsite.project.description.experience_note')}
          </HighlightNote>
        </div>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.event.description.date_label')}>
        <ProjectDescriptionTag>{dateLabel}</ProjectDescriptionTag>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.time_commitment_label')}>
        <span className="flex flex-wrap items-center gap-3">
          {commitmentLabel &&
            <span className="text-base leading-tight text-cs-gray-500">{commitmentLabel}</span>}
          {event.commitmentNotes && <HighlightNote>{event.commitmentNotes}</HighlightNote>}
          <HighlightNote>
            {t('microsite.project.description.schedule_note')}
          </HighlightNote>
        </span>
      </ProjectDescriptionRow>
    </ProjectDescription>
  );

  return (
    <MicrositeShell
      pageTitle={event?.title || t('microsite.event.title')}
      primaryHref={`/event/${eventId}`}
      primaryLabel={t('microsite.event.primary_label')}
      isLoading={isLoading}
      isError={isError}
      notFoundMessage={t('microsite.event.not_found')}
      hasData={!!event}
      hero={{
        image: event?.image?.imageUrl,
        headingPrefix: t('microsite.event.hero.heading_prefix'),
        headingHighlight: t('microsite.event.hero.heading_highlight'),
        subtitlePrefix: t('microsite.project.hero.subtitle_prefix'),
        subtitleBold: t('microsite.project.hero.subtitle_bold'),
        pills: event && (
          <>
            {event.activity && <HeroPill>{event.activity}</HeroPill>}
            {(event.participation_options || []).map((opt) => (
              <HeroPill key={opt}>{opt}</HeroPill>
            ))}
            <HeroPill>{dateLabel}</HeroPill>
          </>
        )
      }}
      description={description}
      showCtas={!event?.completed}
      primaryCta={{body: t('microsite.event.primary_cta.body'), href: '/signup'}}
      interestForm={{itemType: 'event', itemId: event?.id, itemName: event?.title}}
    />
  );
}
